import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';

import JoinWaitlistIcon from './assets/join-waitlist-icon.png';
import ViewAllIcon from './assets/view-all-icon.png';

const THEMES = {
  joinwaitlist: {
    backgroundColor: 'bg-hot-sun-yellow-light-cardbg',
    icon: JoinWaitlistIcon,
    buttonVariant: 'outline' as const,
    ctaText: 'Join Waitlist',
    disabledCtaText: 'Waitlisted',
    border: '',
    title: (courseName?: string) => (
      <>
        <h3 className="text-magical-moonlight-d4 text-sm">
          {`Can't find your `} <b>{courseName ?? 'course'}</b> {' batch?'}
        </h3>
      </>
    ),
  },
  viewall: {
    backgroundColor: 'bg-blue-chaos-light-cardbg',
    icon: ViewAllIcon,
    buttonVariant: 'outline' as const,
    ctaText: 'View All',
    disabledCtaText: 'View Less',
    border: '',
    title: (
      <h3 className="text-magical-moonlight-d4 text-sm">
        More batches available!
      </h3>
    ),
  },
  waitlisted: {
    backgroundColor: 'bg-hot-sun-yellow-light-cardbg',
    icon: JoinWaitlistIcon,
    buttonVariant: 'outline' as const,
    ctaText: null,
    disabledCtaText: null,
    border: 'border-2 border-dashed border-hot-sun',
    title: (courseName?: string) => (
      <>
        <h3 className="text-magical-moonlight-d4 text-base font-semibold mb-1">
          Waitlisted
        </h3>
        <p className="text-magical-moonlight-d4 text-sm">
          You have joined the <b>{courseName ?? 'course'}</b> waitlist.
        </p>
      </>
    ),
  },
} as const;

type ThemeType = keyof typeof THEMES;

export interface JoinWaitlistCardProps {
  onCtaClick?: () => void;
  /**
   * @default false
   */
  isLoading?: boolean;
  bgColor?: string;
  theme?: ThemeType;
  courseName?: string;
  onClick?: () => void;
  /**
   * Force a specific layout (overrides responsive behavior)
   * @default undefined (uses responsive behavior)
   */
  forceLayout?: 'desktop' | 'mobile';
  className?: string;
  isWaitlistActive?: boolean;
}

const JoinWaitlistCard = React.forwardRef<
  HTMLDivElement,
  JoinWaitlistCardProps
>(
  (
    {
      forceLayout,
      className,
      theme = 'joinwaitlist',
      onCtaClick,
      isLoading = false,
      courseName,
      onClick,
      // isWaitlistActive = false,
      ...props
    },
    ref
  ) => {
    const selectedTheme = THEMES[theme];
    const handleClick = onClick ?? onCtaClick;

    return (
      <div
        ref={ref}
        data-testid="join-waitlist-card"
        data-theme={theme}
        className={cn(
          'relative transition-all duration-200 focus:outline-none p-4 md:p-6 rounded-xl md:rounded-3xl w-full  flex flex-row md:flex-col items-center md:justify-center',
          selectedTheme.backgroundColor,
          selectedTheme.border,
          className
        )}
        tabIndex={0}
        role="button"
        onClick={handleClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick?.();
          }
        }}
        {...props}
        // style={{
        //   opacity: isWaitlistActive ? 0.5 : 1,
        //   pointerEvents: isWaitlistActive ? 'none' : 'auto',
        //   cursor: isWaitlistActive ? 'not-allowed' : 'pointer',
        // }}
      >
        <>
          {/* Icon rendering */}
          <div className="flex justify-start items-center w-[4rem] h-[4rem] mr-4 md:mr-0 md:w-full">
            <img src={selectedTheme.icon} alt="Icon" />
          </div>

          <div className="flex flex-col w-full">
            {/* Title */}
            <div className="text-left md:my-3 mt-0 mb-2">
              {typeof selectedTheme.title === 'function'
                ? selectedTheme.title(courseName)
                : selectedTheme.title}
            </div>
            {/* CTA Button */}
            {Boolean(selectedTheme.ctaText) && (
              <div className="flex justify-start">
                <Button
                  onClick={e => {
                    e.stopPropagation();
                    onCtaClick?.();
                  }}
                  disabled={isLoading}
                  variant={selectedTheme.buttonVariant}
                  size="sm"
                >
                  {isLoading ? (
                    'Loading...'
                  ) : (
                    <>
                      {
                        // isWaitlistActive
                        //   ? (selectedTheme?.disabledCtaText ?? '')
                        //   :
                        selectedTheme.ctaText
                      }
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </>

        {/* Shadow/Background element */}
        <div
          className={cn(
            'absolute opacity-60 -top-2 left-2 z-(--z-hide)',
            selectedTheme.backgroundColor,
            (() => {
              if (forceLayout === 'desktop') {
                return 'w-56 h-48 rounded-3xl';
              }
              if (forceLayout === 'mobile') {
                return 'w-[calc(100%-16px)] h-auto rounded-xl';
              }
              // Default responsive behavior
              return 'w-56 h-48 rounded-3xl sm:w-56 sm:h-48 sm:rounded-3xl max-sm:w-[calc(100%-16px)] max-sm:h-auto max-sm:rounded-xl';
            })()
          )}
        />
      </div>
    );
  }
);

JoinWaitlistCard.displayName = 'JoinWaitlistCard';

export { JoinWaitlistCard };
export default JoinWaitlistCard;
